import { useAuthStore } from '@/auth/authStore';
import { setDemoApiAdapter, type ApiOpts } from '@/lib/api';
import { setDemoBlocksAdapter } from '@/pages/learn/blocks/blocksApi';
import {
  blankProject,
  parseProject,
  type BlocksProject,
} from '@/pages/learn/blocks/blocksModel';
import { prepareJtwS4C3P5Project } from '@/pages/learn/blocks/jtwS4C3P5';
import { prepareJtwS4C3P6Project } from '@/pages/learn/blocks/jtwS4C3P6';
import {
  JTW_S4_C1_SEAL_ID,
  JTW_S4_C2_SEAL_ID,
  JTW_S4_C3_SEAL_ID,
  JTW_S4_C4_SEAL_ID,
  JTW_S4_C5_SEAL_ID,
  JTW_S4_C6_SEAL_ID,
} from './journeyWestSeason4';

const QA_KID_ID = 'story-blocks-qa-kid';
const QA_TOKEN = 'qa.eyJzdWIiOiJzdG9yeS1ibG9ja3MtcWEta2lkIn0.qa';
const QA_PROJECTS_KEY = 'airbotix:story-blocks-qa:projects';
const QA_PROJECT_KEY_PREFIX = 'airbotix:story-blocks-qa:project:';
const QA_WINDOW_STATE_TAG = 'airbotix-story-blocks-qa-v1';
let latestQaBlocksProjectId: string | null = null;
const S4_PART_IDS = Array.from({ length: 6 }, (_, chapterIndex) =>
  Array.from(
    { length: 8 },
    (_, partIndex) => `jtw-s4-c${chapterIndex + 1}-p${partIndex + 1}`,
  ),
).flat();
const S4_SEAL_IDS = [
  JTW_S4_C1_SEAL_ID,
  JTW_S4_C2_SEAL_ID,
  JTW_S4_C3_SEAL_ID,
  JTW_S4_C4_SEAL_ID,
  JTW_S4_C5_SEAL_ID,
  JTW_S4_C6_SEAL_ID,
] as const;

function qaJtwS4C3P5Project(): BlocksProject {
  const routePage = (variantId: 'version-a' | 'version-b') => ({
    id: `${variantId}-route-page`,
    background: 'jtw-s4-c3-dual-wukong-test',
    characters: [
      {
        id: 'route-wukong',
        name: variantId === 'version-a' ? 'Version A' : 'Version B',
        emoji: '🐒',
        asset:
          '/story-blocks/journey-to-the-west/characters/wukong-traveller/neutral-v01.png',
        start: { gx: 4, gy: 9, size: 1.8, rot: 0 },
        scripts: [
          {
            id: `${variantId}-route`,
            blocks: [
              { op: 'when_flag' as const },
              { op: 'move_right' as const, n: variantId === 'version-a' ? 4 : 5 },
              { op: 'wait' as const, n: 1 },
              {
                op: 'say' as const,
                text:
                  variantId === 'version-a'
                    ? 'A stops before the reference line.'
                    : 'B crosses the reference line.',
              },
              { op: 'end' as const },
            ],
          },
        ],
      },
      {
        id: 'reference-line',
        name: 'Reference line 8.5',
        emoji: '🚩',
        asset:
          '/story-blocks/journey-to-the-west/props/route-marker/neutral-v01.png',
        start: { gx: 9, gy: 9, size: 1.3, rot: 0 },
        scripts: [],
      },
      {
        id: 'recorder',
        name: 'Route evidence recorder',
        emoji: '📋',
        asset: '/story-blocks/journey-to-the-west/props/map-scroll/neutral-v01.png',
        start: { gx: 15, gy: 5, size: 1.3, rot: 0 },
        scripts: [],
      },
    ],
  });
  const versionA = routePage('version-a');
  const versionB = routePage('version-b');
  const completedP4 = parseProject(
    JSON.stringify({
      version: 1,
      name: 'Journey to the West · Test the same blue message twice',
      lessonId: 'jtw-s4-c3-p4',
      pages: [versionA],
      experiment: {
        kind: 'isolated-variants-v1',
        activeVariantId: 'version-a',
        sharedStartGx: 4,
        sharedTargetGx: 9,
        referenceLineGx: 8.5,
        lockedPrediction: {
          partId: 'jtw-s4-c3-p3',
          choice: 'version-b',
          successSignal: 'crosses-gx8-5',
        },
        variants: [
          { id: 'version-a', label: 'Version A', pages: [versionA] },
          { id: 'version-b', label: 'Version B', pages: [versionB] },
        ],
        runLedger: [
          {
            variantId: 'version-a',
            endpointGx: 8,
            successSignal: 'route-a-gx8',
            resetToHome: true,
            completedAt: 'qa-route-a',
          },
          {
            variantId: 'version-b',
            endpointGx: 9,
            successSignal: 'route-b-gx9',
            resetToHome: true,
            completedAt: 'qa-route-b',
          },
        ],
      },
    }),
  );

  return prepareJtwS4C3P5Project(completedP4)!;
}

function qaJtwS4C3P6Project(): BlocksProject {
  const p5 = qaJtwS4C3P5Project();
  for (const variant of p5.experiment?.variants ?? []) {
    const page = variant.pages[0];
    const tester = page.characters.find((actor) => actor.id === 'blue-tester');
    const stamp = page.characters.find((actor) => actor.id === `stamp-${variant.id.at(-1)}`);
    if (tester?.scripts[0]) {
      tester.scripts[0].blocks.push({ op: 'send_message', n: 5 }, { op: 'end' });
    }
    if (stamp?.scripts[0]) {
      stamp.scripts[0].blocks.push(
        { op: 'show' },
        { op: 'say', text: variant.id === 'version-a' ? 'A seal is above.' : 'B seal is below.' },
        { op: 'end' },
      );
    }
  }
  return prepareJtwS4C3P6Project(p5)!;
}

export function qaBlocksProject(lessonId?: string): BlocksProject {
  if (lessonId === 'jtw-s1-c1-p5') {
    return parseProject(
      JSON.stringify({
        version: 1,
        name: 'Journey to the West · Two sincere greetings',
        lessonId,
        pages: [
          {
            id: 'jtw-c1-p5-page',
            background: 'jtw-s1-c1-flower-fruit-stone',
            characters: [
              {
                id: 'stone-monkey',
                name: 'Stone Monkey',
                emoji: '🐵',
                asset:
                  '/story-blocks/journey-to-the-west/characters/stone-monkey/neutral-v01.png',
                start: { gx: 8, gy: 9, size: 3, rot: 0 },
                scripts: [
                  {
                    id: 'stone-monkey-first-greeting',
                    blocks: [
                      { op: 'when_flag' },
                      { op: 'hide' },
                      { op: 'play_sound', n: 2 },
                      { op: 'show' },
                      { op: 'end' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      }),
    );
  }

  if (lessonId === 'jtw-s4-c3-p5') return qaJtwS4C3P5Project();
  if (lessonId === 'jtw-s4-c3-p6') return qaJtwS4C3P6Project();

  return { ...blankProject('Journey to the West · Story Blocks QA'), lessonId };
}

export function storyBlocksQaProjectFallback(): string | null {
  return latestQaBlocksProjectId;
}

/**
 * Install a durable, refresh-safe local Story Blocks session for browser QA.
 *
 * This is deliberately double-gated by Vite DEV mode and an explicit env flag.
 * It never ships in a production build, never reads or writes a database and
 * never turns a QA receipt into production progress. It exists so a local S4
 * route audit can follow real links and refresh pages without depending on a
 * separately running backend.
 */
export function installStoryBlocksQaSession(): boolean {
  if (!import.meta.env.DEV || import.meta.env.VITE_STORY_BLOCKS_QA_SESSION !== '1') {
    return false;
  }

  useAuthStore.setState({
    tokens: { user: null, kid: QA_TOKEN },
    bootstrapped: true,
  });

  type QaProjectMeta = {
    id: string;
    title: string;
    kind: 'blocks';
    status: 'active';
    created_at: string;
    updated_at: string;
  };
  type QaSavedProject = {
    project: BlocksProject;
    version: number;
    history: { past: []; future: [] };
    otherFiles: [];
    storyProgress: { schemaVersion: 1; completed: Record<string, { completedAt: string }> };
  };
  type QaWindowState = {
    tag: typeof QA_WINDOW_STATE_TAG;
    projects: QaProjectMeta[];
    saved: Record<string, QaSavedProject>;
  };
  let projectsInThisTab: QaProjectMeta[] = [];
  const savedInThisTab = new Map<string, QaSavedProject>();
  const completedParts = new Map<string, { completed_at: string; evidence: unknown }>();
  const litSealIds = new Set<string>();
  const emptyWindowState = (): QaWindowState => ({
    tag: QA_WINDOW_STATE_TAG,
    projects: [],
    saved: {},
  });
  const readWindowState = (): QaWindowState => {
    try {
      const value = JSON.parse(window.name || '{}') as Partial<QaWindowState>;
      if (value.tag !== QA_WINDOW_STATE_TAG) return emptyWindowState();
      return {
        tag: QA_WINDOW_STATE_TAG,
        projects: Array.isArray(value.projects) ? value.projects : [],
        saved: value.saved && typeof value.saved === 'object' ? value.saved : {},
      };
    } catch {
      return emptyWindowState();
    }
  };
  const writeWindowState = (state: QaWindowState) => {
    window.name = JSON.stringify(state);
  };
  const readProjects = (): QaProjectMeta[] => {
    if (projectsInThisTab.length > 0) return projectsInThisTab;
    try {
      const value = JSON.parse(localStorage.getItem(QA_PROJECTS_KEY) ?? '[]') as unknown;
      if (Array.isArray(value) && value.length > 0) {
        projectsInThisTab = value as QaProjectMeta[];
        return projectsInThisTab;
      }
    } catch {
      // The in-app browser can isolate localStorage between localhost navigations.
    }
    projectsInThisTab = readWindowState().projects;
    return projectsInThisTab;
  };
  const writeProjects = (projects: QaProjectMeta[]) => {
    projectsInThisTab = projects;
    try {
      localStorage.setItem(QA_PROJECTS_KEY, JSON.stringify(projects));
    } catch {
      // window.name below is the refresh-safe QA fallback.
    }
    const state = readWindowState();
    writeWindowState({ ...state, projects });
  };
  const readSavedProject = (projectId: string): QaSavedProject | null => {
    const inMemory = savedInThisTab.get(projectId);
    if (inMemory) return inMemory;
    const tabSaved = readWindowState().saved[projectId];
    if (tabSaved) {
      const parsed = { ...tabSaved, project: parseProject(JSON.stringify(tabSaved.project)) };
      savedInThisTab.set(projectId, parsed);
      return parsed;
    }
    try {
      const value = localStorage.getItem(`${QA_PROJECT_KEY_PREFIX}${projectId}`);
      if (value) {
        const saved = JSON.parse(value) as QaSavedProject;
        const parsed = { ...saved, project: parseProject(JSON.stringify(saved.project)) };
        savedInThisTab.set(projectId, parsed);
        return parsed;
      }
    } catch {
      // Fall through to the tab-scoped backup.
    }
    return null;
  };
  const writeSavedProject = (projectId: string, saved: QaSavedProject) => {
    const existingSaved = savedInThisTab.get(projectId) ?? readWindowState().saved[projectId];
    const mergedLedger = existingSaved
      ? mergeC3P6RunLedger(saved.project, existingSaved.project)
      : null;
    const savedToWrite = mergedLedger && saved.project.experiment
      ? {
          ...saved,
          project: {
            ...saved.project,
            experiment: { ...saved.project.experiment, runLedger: mergedLedger },
          },
        }
      : saved;
    savedInThisTab.set(projectId, savedToWrite);
    try {
      localStorage.setItem(`${QA_PROJECT_KEY_PREFIX}${projectId}`, JSON.stringify(savedToWrite));
    } catch {
      // window.name below is the refresh-safe QA fallback.
    }
    const windowState = readWindowState();
    writeWindowState({
      ...windowState,
      saved: { ...windowState.saved, [projectId]: savedToWrite },
    });
    const now = new Date().toISOString();
    const projects = readProjects();
    const existing = projects.find((meta) => meta.id === projectId);
    writeProjects([
      {
        id: projectId,
        title: existing?.title ?? savedToWrite.project.name,
        kind: 'blocks',
        status: 'active',
        created_at: existing?.created_at ?? now,
        updated_at: now,
      },
      ...projects.filter((meta) => meta.id !== projectId),
    ]);
  };
  const mergeC3P6RunLedger = (base: BlocksProject, incoming: BlocksProject) => {
    if (
      base.lessonId !== 'jtw-s4-c3-p6' ||
      incoming.lessonId !== 'jtw-s4-c3-p6' ||
      !base.experiment ||
      !incoming.experiment
    ) {
      return null;
    }
    const receipts = new Map(
      base.experiment.runLedger.map((entry) => [
        `${entry.variantId}:${entry.phase ?? 'none'}`,
        entry,
      ]),
    );
    for (const entry of incoming.experiment.runLedger) {
      const key = `${entry.variantId}:${entry.phase ?? 'none'}`;
      const existing = receipts.get(key);
      receipts.set(key, {
        ...existing,
        ...entry,
        ...((existing?.visibleActorIds || entry.visibleActorIds)
          ? {
              visibleActorIds: Array.from(
                new Set([...(existing?.visibleActorIds ?? []), ...(entry.visibleActorIds ?? [])]),
              ),
            }
          : {}),
      });
    }
    return [...receipts.values()];
  };
  setDemoBlocksAdapter({
    load: async (projectId) => {
      latestQaBlocksProjectId = projectId;
      const lessonId = new URLSearchParams(window.location.search).get('storyPart') ?? undefined;
      const saved = readSavedProject(projectId);
      // A project id owns its saved lesson. Storybook pages intentionally do not
      // carry `storyPart` in their query string, so comparing the saved lesson to
      // the current URL used to replace a completed Studio project with a blank
      // one as soon as the child returned to the book.
      if (saved) return saved;
      const seeded: QaSavedProject = {
        project: qaBlocksProject(lessonId),
        version: 1,
        history: { past: [], future: [] },
        otherFiles: [],
        storyProgress: { schemaVersion: 1, completed: {} },
      };
      writeSavedProject(projectId, seeded);
      return seeded;
    },
    save: async ({ projectId, project, history, storyProgress, version }) => {
      const current = readSavedProject(projectId);
      if (current && current.version > version) {
        const mergedLedger = mergeC3P6RunLedger(current.project, project);
        if (
          mergedLedger &&
          JSON.stringify(mergedLedger) !== JSON.stringify(current.project.experiment?.runLedger)
        ) {
          const mergedProject = {
            ...current.project,
            experiment: { ...current.project.experiment!, runLedger: mergedLedger },
          };
          const nextVersion = current.version + 1;
          writeSavedProject(projectId, {
            ...current,
            project: mergedProject,
            version: nextVersion,
            storyProgress: storyProgress ?? current.storyProgress,
          });
          void history;
          return { status: 'saved', version: nextVersion };
        }
        return {
          status: 'kept-newest',
          project: current.project,
          version: current.version,
          storyProgress: current.storyProgress,
        };
      }
      let projectToSave = project;
      const mergedLedger = current ? mergeC3P6RunLedger(project, current.project) : null;
      if (mergedLedger && project.experiment) {
        projectToSave = {
          ...project,
          experiment: { ...project.experiment, runLedger: mergedLedger },
        };
      }
      const nextVersion = version + 1;
      writeSavedProject(projectId, {
        project: projectToSave,
        version: nextVersion,
        history: { past: [], future: [] },
        otherFiles: [],
        storyProgress: storyProgress ?? { schemaVersion: 1, completed: {} },
      });
      void history;
      return { status: 'saved', version: nextVersion };
    },
  });

  setDemoApiAdapter(async <T>(path: string, opts: ApiOpts): Promise<T> => {
    if (path === '/auth/me') {
      return {
        role: 'kid',
        kid: {
          id: QA_KID_ID,
          nickname: 'Story Explorer',
          avatar_id: 'monkey',
          age: 8,
          family_id: 'story-blocks-qa-family',
          is_ephemeral: false,
          claim_code: null,
        },
      } as T;
    }

    if (opts.method !== 'POST' && /\/kids\/[^/]+\/projects(?:\?|$)/.test(path)) {
      return readProjects() as T;
    }

    if (/^\/story-parts\/journey-to-the-west-s\d$/.test(path)) {
      return {
        story_line_id: path.split('/').at(-1),
        completed: [...completedParts].map(([part_id, entry]) => ({ part_id, ...entry })),
        unlocked_part_ids: path.endsWith('-s4') ? S4_PART_IDS : [],
        chapter_seals: S4_SEAL_IDS.map((seal_id, index) => ({
          seal_id,
          chapter_code: `C${index + 1}`,
          lit: litSealIds.has(seal_id),
          missing: [],
        })),
      } as T;
    }

    if (/^\/story-parts\/journey-to-the-west-s\d\/parts\//.test(path)) {
      const partId = path.split('/').at(-1) ?? '';
      const completed_at = new Date().toISOString();
      const body = opts.body && typeof opts.body === 'object' ? opts.body as { evidence?: unknown } : {};
      completedParts.set(partId, { completed_at, evidence: body.evidence ?? {} });
      const chapter = Number(partId.match(/-c([1-6])-p8$/)?.[1] ?? 0);
      const sealId = S4_SEAL_IDS[chapter - 1];
      if (sealId) litSealIds.add(sealId);
      return { part_id: partId, completed_at } as T;
    }

    if (path === '/projects' && opts.method === 'POST') {
      const id = `story-blocks-qa-${crypto.randomUUID()}`;
      latestQaBlocksProjectId = id;
      const body = opts.body && typeof opts.body === 'object' ? opts.body as Record<string, unknown> : {};
      const now = new Date().toISOString();
      writeProjects([
        {
          id,
          title: typeof body.title === 'string' ? body.title : 'Journey to the West · Story Blocks QA',
          kind: 'blocks',
          status: 'active',
          created_at: now,
          updated_at: now,
        },
        ...readProjects().filter((meta) => meta.id !== id),
      ]);
      return { id } as T;
    }

    throw new Error(`Story Blocks QA adapter has no response for ${opts.method ?? 'GET'} ${path}`);
  });

  return true;
}
